/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Box,
    Container,
    Flex,
    Link,
    Stack,
    Text,
    ChakraProvider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'
import theme from '@salesforce/retail-react-app/app/theme'

import {LinkBreakIcon} from '../../components/icons'
import {colors} from '../../constants'

import Seo from '../seo'

// <Error> is rendered when:
//
// 1. A user requests a page that is not routable from `app/routes.jsx`.
// 2. A routed component throws an error in `getProps()`.
// 3. A routed component throws an error in `render()`.
//
// It must not throw an error. Keep it as simple as possible.

const Error = (props) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const {message, stack} = props
    return (
        <ChakraProvider theme={theme}>
            <Seo></Seo>
            <Box
                flex="1"
                minWidth="100%"
                minH="100%"
                width="100%"
                bgGradient={colors.redBgGradient}
            >
                <Container
                    width={['342px', '490px']}
                    padding={['12px', 0]}
                    margin={['100px auto 171px', '140px auto 343px']}
                >
                    <Box align="center">
                        <LinkBreakIcon
                            width={{base: '50px', lg: '62.5px'}}
                            height={{base: '34px', lg: '42.5px'}}
                        />
                    </Box>

                    <Text
                        align="center"
                        fontSize={{base: '32px', lg: '56px'}}
                        fontFamily="Raleway"
                        fontWeight="400"
                        mt="48px"
                    >
                        Something went wrong
                    </Text>
                    <Stack spacing="48px" alignItems="center" mt="24px">
                        <Text
                            align="center"
                            fontSize={{base: '14px', lg: '16px'}}
                            fontFamily="Roboto"
                            fontWeight="300"
                            lineHeight={{base: '20px', lg: '24px'}}
                        >
                            Sorry, the page you&apos;re looking for doesn&apos;t
                            exist or has been moved
                        </Text>

                        <Flex gap="32px" padding="0px" alignItems="center">
                            <Link
                                text="Contact Us"
                                type="tertiary"
                                color={colors.black}
                                textDecoration="underline"
                                fontSize="16px"
                                fontWeight="300"
                                fontFamily="Roboto"
                                _hover={{
                                    background: 'transparent',
                                    backgroundColor: 'transparent'
                                }}
                                _focus={{
                                    background: 'transparent',
                                    backgroundColor: 'transparent'
                                }}
                                href="/pages/contact"
                            >
                                Contact Us
                            </Link>
                        </Flex>
                    </Stack>
                </Container>
            </Box>
        </ChakraProvider>
    )
}

Error.propTypes = {
    // JavaScript error stack trace: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/stack
    stack: PropTypes.string,
    // HTTP status code: https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
    status: PropTypes.number,
    // A description of the error, if available
    message: PropTypes.string
}

export default Error
